import { useEffect, useState } from "react";
import { Plus, Trash, VideoCamera, PencilSimple, Eye, EyeSlash, QrCode, Copy, Monitor, Printer } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api, { formatApiError, getWithRetry } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import ConfirmDialog from "../ConfirmDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const EMPTY = { name: "", camera_url: "", camera_type: "none", camera_enabled: true };

export default function CourtsPanel() {
  const [courts, setCourts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [qrCourt, setQrCourt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const origin = window.location.origin;
  const hubUrl = (id) => `${origin}/court/${id}`;
  const screenUrl = (id) => `${origin}/court/${id}/screen`;
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Enlace copiado"); };

  const printQR = () => {
    if (!qrCourt) return;
    const svg = document.querySelector('[data-testid="court-qr"]');
    const svgHtml = svg ? svg.outerHTML : "";
    const w = window.open("", "_blank", "width=480,height=680");
    if (!w) { toast.error("Permite las ventanas emergentes para imprimir"); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>QR ${qrCourt.name}</title>
      <style>
        @media print { @page { margin: 16mm; } }
        html,body{height:100%;}
        body{margin:0;font-family:Arial,Helvetica,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:32px;}
        h1{font-size:30px;font-weight:800;text-transform:uppercase;letter-spacing:3px;margin:0 0 28px;text-align:center;width:100%;}
        .qr{display:inline-block;line-height:0;}
        .qr svg{width:300px;height:300px;}
        p{margin:24px 0 0;color:#666;font-size:12px;word-break:break-all;max-width:340px;}
        .brand{margin-top:8px;font-size:11px;letter-spacing:2px;color:#999;text-transform:uppercase;}
      </style></head>
      <body>
        <h1>${qrCourt.name}</h1>
        <div class="qr">${svgHtml}</div>
        <p>${hubUrl(qrCourt.id)}</p>
        <div class="brand">Padel Live</div>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 350);
  };

  const load = () => getWithRetry("/courts").then((r) => setCourts(r.data)).catch(() => toast.error("No se pudieron cargar las canchas. Reintenta.")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return;
    try {
      if (editing) await api.put(`/courts/${editing}`, form);
      else await api.post("/courts", form);
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
      toast.success("Cancha guardada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    await api.delete(`/courts/${id}`);
    load();
    toast.success("Cancha eliminada");
  };

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, camera_url: c.camera_url || "", camera_type: c.camera_type || "none", camera_enabled: c.camera_enabled !== false }); setEditing(c.id); setOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-3xl font-extrabold uppercase">Canchas</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-court-btn" onClick={openNew} className="bg-padel-blue text-black font-heading uppercase tracking-widest rounded-none hover:bg-padel-green">
              <Plus size={18} className="mr-1" /> Nueva cancha
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#131417] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">{editing ? "Editar" : "Nueva"} cancha</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <Input data-testid="court-name-input" placeholder="Nombre (ej. Cancha 1)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-white/15" />
              <div>
                <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2 flex items-center gap-1"><VideoCamera size={14} /> Cámara (Wyze)</p>
                <Select value={form.camera_type} onValueChange={(v) => setForm({ ...form, camera_type: v })}>
                  <SelectTrigger data-testid="court-camera-type" className="bg-[#0A0A0C] border-white/15"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cámara</SelectItem>
                    <SelectItem value="hls">Stream HLS (.m3u8)</SelectItem>
                    <SelectItem value="iframe">Embed / iframe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.camera_type !== "none" && (
                <>
                  <Input data-testid="court-camera-url" placeholder={form.camera_type === "hls" ? "https://…/stream.m3u8" : "URL para iframe"} value={form.camera_url} onChange={(e) => setForm({ ...form, camera_url: e.target.value })} className="bg-[#0A0A0C] border-white/15" />
                  <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 px-4 h-12">
                    <span className="text-sm flex items-center gap-2">
                      {form.camera_enabled ? <Eye size={16} className="text-padel-green" /> : <EyeSlash size={16} className="text-muted-foreground" />}
                      Visualizar cámara
                    </span>
                    <Switch data-testid="court-camera-enabled" checked={form.camera_enabled} onCheckedChange={(v) => setForm({ ...form, camera_enabled: v })} />
                  </div>
                  {!form.camera_enabled && <p className="text-xs text-muted-foreground">La cámara está configurada pero no se mostrará en pantalla.</p>}
                </>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="save-court-btn" onClick={save} className="bg-padel-green text-black font-heading uppercase tracking-widest rounded-none">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-padel-blue font-heading uppercase tracking-widest animate-pulse-glow">Cargando canchas…</p>
      ) : courts.length === 0 ? (
        <p className="text-muted-foreground">Aún no hay canchas. Crea la primera.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courts.map((c) => (
            <div key={c.id} data-testid={`court-card-${c.id}`} className="bg-[#131417] border border-white/10 p-6 hover:-translate-y-1 transition-transform">
              <div className="flex items-start justify-between">
                <h3 className="font-heading text-2xl font-bold uppercase">{c.name}</h3>
                <div className="flex gap-1">
                  <button data-testid={`qr-court-${c.id}`} onClick={() => setQrCourt(c)} className="p-2 text-muted-foreground hover:text-padel-green"><QrCode size={18} /></button>
                  <button onClick={() => openEdit(c)} className="p-2 text-muted-foreground hover:text-padel-blue"><PencilSimple size={18} /></button>
                  <button data-testid={`delete-court-${c.id}`} onClick={() => setConfirm(c)} className="p-2 text-muted-foreground hover:text-padel-danger"><Trash size={18} /></button>
                </div>
              </div>
              <p className="mt-3 text-xs tracking-widest uppercase flex items-center gap-2">
                {c.camera_type === "none" ? (
                  <><VideoCamera size={16} className="text-muted-foreground" /> Sin cámara</>
                ) : c.camera_enabled === false ? (
                  <><EyeSlash size={16} className="text-muted-foreground" /> Cámara {c.camera_type.toUpperCase()} · oculta</>
                ) : (
                  <><Eye size={16} className="text-padel-green" /> Cámara {c.camera_type.toUpperCase()} · visible</>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* QR dialog */}
      <Dialog open={!!qrCourt} onOpenChange={(o) => !o && setQrCourt(null)}>
        <DialogContent className="bg-[#131417] border-white/10 text-white max-w-sm overflow-hidden">
          <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">QR · {qrCourt?.name}</DialogTitle></DialogHeader>
          {qrCourt && (
            <div className="space-y-4 py-1 min-w-0 w-full">
              <div className="flex justify-center">
                <div className="bg-white p-3 rounded-lg">
                  <QRCodeSVG data-testid="court-qr" value={hubUrl(qrCourt.id)} size={172} level="M" />
                </div>
              </div>
              <p className="text-center text-xs text-muted-foreground px-2">Escanea para configurar el partido, llevar el marcador o ver el resultado de esta cancha.</p>

              <div className="space-y-2 min-w-0">
                <div className="flex items-center gap-2 bg-[#0A0A0C] border border-white/10 px-3 h-10 min-w-0">
                  <QrCode size={15} className="text-padel-green shrink-0" />
                  <span className="text-xs truncate flex-1 min-w-0">{hubUrl(qrCourt.id)}</span>
                  <button data-testid="copy-hub-link" onClick={() => copy(hubUrl(qrCourt.id))} className="text-muted-foreground hover:text-padel-blue shrink-0"><Copy size={15} /></button>
                </div>
                <div className="flex items-center gap-2 bg-[#0A0A0C] border border-white/10 px-3 h-10 min-w-0">
                  <Monitor size={15} className="text-padel-blue shrink-0" />
                  <span className="text-xs truncate flex-1 min-w-0">{screenUrl(qrCourt.id)}</span>
                  <button data-testid="copy-screen-link" onClick={() => copy(screenUrl(qrCourt.id))} className="text-muted-foreground hover:text-padel-blue shrink-0"><Copy size={15} /></button>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">El enlace de pantalla completa siempre muestra el partido actual de la cancha y se actualiza solo, sin reiniciar.</p>
              </div>

              <Button data-testid="print-qr-btn" onClick={printQR} className="w-full bg-padel-green text-black font-heading uppercase tracking-widest rounded-none hover:bg-padel-blue">
                <Printer size={18} className="mr-2" /> Imprimir QR
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Eliminar cancha"
        description={confirm ? `¿Seguro que quieres eliminar la cancha "${confirm.name}"? Esta acción no se puede deshacer.` : ""}
        onConfirm={() => confirm && remove(confirm.id)}
      />
    </div>
  );
}
